import { getQuestionButtonSizeVars } from "../FreeResponse/visor/SubmitAnswerButton";

// Initial "Button Style" state of a TrueFalse box (see TrueFalse.getInitialState).
// These values were persisted by every instance before the True/False option
// buttons honoured the setting, so a value equal to its default is treated as
// "not customised" and the option keeps its original neutral look.
export const TRUE_FALSE_BUTTON_DEFAULTS = {
  buttonBackgroundColor: "#ede9fe",
  buttonTextColor: "#111827",
  buttonBorderColor: "#ede9fe",
  buttonBorderWidth: 1,
  buttonBorderRadius: 4,
  buttonPadding: 8,
  buttonFontSize: 14,
  buttonFontWeight: "normal",
  buttonHoverBackgroundColor: "#ede9fe",
};

const isCustomised = (state, key) => {
  const value = state[key];
  if (value === undefined || value === null || value === "") {
    return false;
  }
  return String(value).toLowerCase() !== String(TRUE_FALSE_BUTTON_DEFAULTS[key]).toLowerCase();
};

const em = (px) => `${Number(px) / 14}em`;

const hasValue = (value) => value !== undefined && value !== null && value !== "";

// Original neutral look of a True/False option (_truefalse.scss fallbacks).
export const TRUE_FALSE_OPTION_LOOK = {
  buttonBackgroundColor: "#ffffff",
  buttonTextColor: "#6b7280",
  buttonBorderColor: "#e9eaeb",
  buttonHoverBackgroundColor: "#ffffff",
};

// Colour settings that can be set per option ("Doğru Düğmesi" / "Yanlış
// Düğmesi"). Stored as `true<Key>` / `false<Key>`, e.g. trueButtonBackgroundColor.
export const TRUE_FALSE_OPTION_COLOR_KEYS = Object.keys(TRUE_FALSE_OPTION_LOOK);

export const getOptionStateKey = (kind, key) => `${kind}${key[0].toUpperCase()}${key.slice(1)}`;

// Colour of one option: its own setting, else the shared "Button Style" colour
// when customised, else undefined (the SCSS keeps the original design).
export function getTrueFalseOptionColor(state = {}, kind, key) {
  const own = kind ? state[getOptionStateKey(kind, key)] : undefined;
  if (hasValue(own)) {
    return own;
  }
  return isCustomised(state, key) ? state[key] : undefined;
}

// Same, but never undefined — what the toolbar colour pickers display.
export const getTrueFalseOptionDisplayColor = (state, kind, key) =>
  getTrueFalseOptionColor(state, kind, key) || TRUE_FALSE_OPTION_LOOK[key];

// CSS custom properties for one True/False option button (kind "true" |
// "false"), set inline on it; _truefalse.scss reads them and falls back to the
// original design when unset. Shared by the editor template and the visor so
// both render identically.
export function getTrueFalseOptionVars(state = {}, kind) {
  const vars = {};
  const background = getTrueFalseOptionColor(state, kind, "buttonBackgroundColor");
  const hoverBackground = getTrueFalseOptionColor(state, kind, "buttonHoverBackgroundColor");
  const text = getTrueFalseOptionColor(state, kind, "buttonTextColor");
  const border = getTrueFalseOptionColor(state, kind, "buttonBorderColor");
  if (background) {
    vars["--tf-btn-bg"] = background;
  }
  if (hoverBackground) {
    vars["--tf-btn-hover-bg"] = hoverBackground;
  }
  if (text) {
    vars["--tf-btn-color"] = text;
    // The selected option is told apart by its filled radio dot only, so it
    // keeps the author's text colour.
    vars["--tf-btn-selected-color"] = text;
    vars["--tf-btn-selected-border"] = text;
  }
  if (border) {
    vars["--tf-btn-border"] = border;
    // A chosen border colour applies to the selected option too.
    vars["--tf-btn-selected-border"] = border;
  }
  if (isCustomised(state, "buttonBorderWidth")) {
    vars["--tf-btn-border-w"] = `${Number(state.buttonBorderWidth)}px`;
  }
  if (isCustomised(state, "buttonBorderRadius")) {
    vars["--tf-btn-radius"] = em(state.buttonBorderRadius);
  }
  if (isCustomised(state, "buttonPadding")) {
    vars["--tf-btn-pad-x"] = em(state.buttonPadding);
  }
  if (isCustomised(state, "buttonFontSize")) {
    vars["--tf-btn-font"] = em(state.buttonFontSize);
  }
  if (isCustomised(state, "buttonFontWeight")) {
    vars["--tf-btn-weight"] = state.buttonFontWeight;
  }
  return vars;
}

// Inline style for the visor action buttons (check / try again / see answer).
export function getTrueFalseActionButtonStyle(state = {}) {
  return {
    backgroundColor: state.buttonBackgroundColor || "#ede9fe",
    color: state.buttonTextColor || "#111827",
    borderColor: state.buttonBorderColor || "#ede9fe",
    borderWidth: `${state.buttonBorderWidth ?? 1}px`,
    borderRadius: em(state.buttonBorderRadius ?? 4),
    borderStyle: "solid",
    fontWeight: state.buttonFontWeight || "normal",
    cursor: "pointer",
    margin: "2px",
    transition: "background-color 0.2s ease",
    ...getQuestionButtonSizeVars(state),
  };
}
